import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronRight,
  Download,
  FileText,
  GitBranch,
  Scale,
  Sparkles,
} from 'lucide-react';
import './DecisionInterneDetail.css';

/* ────────────────────────── Types ────────────────────────── */

interface Partie {
  nom: string;
  qualite: string;
  avocat?: string;
}

interface EtapeProcedure {
  date: string;
  acte: string;
}

interface DecisionComplete {
  id: string;
  numero: string;
  numeroRG: string;
  date: string;
  juridiction: string;
  sigle: string;
  formation: string;
  president: string;
  greffier: string;
  matiere: string;
  typeDecision: string;
  instance: string;
  objet: string;
  synthese: string;
  parties: Partie[];
  procedure: EtapeProcedure[];
  demandes: string[];
  textesCites: string[];
  dispositif: string[];
  motsCles: string[];
  affaire: string;
  estInterne?: boolean;
}

/* ────────────────────────── Données mock ────────────────────────── */

const DECISIONS: DecisionComplete[] = [
  {
    id: 'nat-1187',
    numero: '1187/2024', numeroRG: 'RG n° 2024/1187',
    date: '2024-11-14', juridiction: "Tribunal de commerce d'Abidjan", sigle: 'TCA',
    formation: '2e chambre', president: 'Mme N\u2019GUESSAN Aya', greffier: 'M. TRAORÉ Issa',
    matiere: 'Sûretés et garanties', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Appel en garantie sur caution bancaire solidaire",
    synthese: "Le tribunal retient l'engagement de caution solidaire souscrit par la banque et la condamne au paiement du principal réclamé, avec exécution provisoire partielle.",
    parties: [
      { nom: 'SOCIÉTÉ IVOMAT SARL', qualite: 'Demanderesse', avocat: 'Me BAMBA Salif' },
      { nom: "BANQUE ATLANTIQUE CÔTE D'IVOIRE", qualite: 'Défenderesse', avocat: 'Cabinet DIALLO & Associés' },
    ],
    procedure: [
      { date: '2024-03-12', acte: "Assignation délivrée par la demanderesse devant le Tribunal de commerce d'Abidjan" },
      { date: '2024-05-20', acte: "Constitution de la défenderesse et dépôt de conclusions en réponse" },
      { date: '2024-09-10', acte: "Audience de plaidoirie — les parties ont développé leurs moyens respectifs" },
    ],
    demandes: [
      "condamner la BANQUE ATLANTIQUE CÔTE D'IVOIRE au paiement de la somme de 51 000 000 FCFA",
      "assortir la condamnation de l'exécution provisoire",
      "condamner la défenderesse aux entiers dépens",
    ],
    textesCites: ["l'Acte uniforme portant organisation des sûretés (art. 13 et suivants)"],
    dispositif: [
      "Reçoit la demanderesse en son action",
      "Condamne la BANQUE ATLANTIQUE CÔTE D'IVOIRE à payer la somme de 51 000 000 FCFA",
      "Ordonne l'exécution provisoire à hauteur de 50 %",
      "Condamne la défenderesse aux dépens",
    ],
    motsCles: ['caution bancaire', 'sûreté', 'exécution provisoire'],
    affaire: 'AFF-IVOMAT-BATL', estInterne: true,
  },
  {
    id: 'nat-412',
    numero: '412/2025', numeroRG: 'RG n° 2025/0412',
    date: '2025-06-04', juridiction: "Cour d'appel de commerce d'Abidjan", sigle: 'CAA',
    formation: '1re chambre', president: 'M. KONÉ Sékou', greffier: 'Mme DIABATÉ Mariam',
    matiere: 'Sûretés et garanties', typeDecision: 'Arrêt', instance: 'Appel',
    objet: "Expertise comptable ordonnée avant dire droit",
    synthese: "La cour ordonne une expertise comptable pour déterminer le solde exigible et sursoit à statuer sur le surplus.",
    parties: [
      { nom: "BANQUE ATLANTIQUE CÔTE D'IVOIRE", qualite: 'Appelante' },
      { nom: 'SOCIÉTÉ IVOMAT SARL', qualite: 'Intimée' },
    ],
    procedure: [
      { date: '2025-01-15', acte: "Déclaration d'appel formée par la BANQUE ATLANTIQUE CÔTE D'IVOIRE" },
      { date: '2025-03-22', acte: "Conclusions d'intimée déposées par la SOCIÉTÉ IVOMAT SARL" },
      { date: '2025-05-12', acte: "Audience — la cour ordonne une expertise avant dire droit" },
    ],
    demandes: ["infirmer le jugement en ce qu'il a condamné l'appelante au paiement", "à titre subsidiaire, ordonner une expertise"],
    textesCites: ["l'Acte uniforme portant organisation des sûretés (art. 13)", "le Code de procédure civile (art. 145)"],
    dispositif: ["Ordonne une expertise comptable", "Désigne M. KOUAMÉ comme expert", "Sursoit à statuer sur le surplus"],
    motsCles: ['expertise', 'sûreté', 'appel'],
    affaire: 'AFF-IVOMAT-BATL', estInterne: true,
  },
  {
    id: 'nat-261',
    numero: '261/2025', numeroRG: 'RG n° 2025/0261',
    date: '2025-03-11', juridiction: 'Tribunal de commerce de Bouaké', sigle: 'TCB',
    formation: 'Chambre unique', president: 'M. TOURÉ Abdoulaye', greffier: 'M. KONATÉ Youssouf',
    matiere: 'Recouvrement de créance', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Validité de la mise en demeure et intérêts de retard",
    synthese: "La juridiction confirme le caractère exigible de la créance après une mise en demeure régulièrement adressée.",
    parties: [
      { nom: 'SOCIÉTÉ KANZA DISTRIBUTION', qualite: 'Demanderesse' },
      { nom: 'ETS KOUADIO FRÈRES', qualite: 'Défenderesse' },
    ],
    procedure: [
      { date: '2024-12-01', acte: "Mise en demeure adressée par lettre recommandée" },
      { date: '2025-01-20', acte: "Assignation devant le Tribunal de commerce de Bouaké" },
    ],
    demandes: ["condamner la défenderesse au paiement de la créance", "assortir la condamnation des intérêts de retard"],
    textesCites: ["le Code civil (art. 1153)"],
    dispositif: ["Condamne la défenderesse au paiement de la créance", "Dit que la somme portera intérêts au taux légal"],
    motsCles: ['recouvrement', 'mise en demeure', 'intérêts'],
    affaire: 'AFF-KANZA-KOUADIO',
  },
  {
    id: 'nat-97',
    numero: '97/2026', numeroRG: 'RG n° 2026/0097',
    date: '2026-01-22', juridiction: "Cour d'appel d'Abidjan", sigle: 'CA',
    formation: 'Chambre civile et commerciale', president: 'M. DIARRA Moussa', greffier: 'Mme KONAN Aya',
    matiere: 'Droit des sociétés', typeDecision: 'Arrêt', instance: 'Appel',
    objet: "Responsabilité du gérant pour faute de gestion",
    synthese: "La cour retient une faute de gestion caractérisée et confirme l'obligation de réparer le préjudice subi par la société.",
    parties: [
      { nom: 'SOCIÉTÉ IVOIRE LOGISTIQUE', qualite: 'Appelante' },
      { nom: 'M. KOUASSI YAO', qualite: 'Intimé' },
    ],
    procedure: [
      { date: '2025-09-05', acte: "Déclaration d'appel" },
      { date: '2025-11-18', acte: "Échange de conclusions" },
      { date: '2026-01-10', acte: "Audience de plaidoirie" },
    ],
    demandes: ["confirmer la condamnation du gérant pour faute de gestion"],
    textesCites: ["l'Acte uniforme relatif au droit des sociétés commerciales (art. 161)"],
    dispositif: ["Confirme le jugement en toutes ses dispositions", "Condamne l'intimé aux dépens d'appel"],
    motsCles: ['faute de gestion', 'responsabilité', 'gérant'],
    affaire: 'AFF-IVOIRE-KOUASSI',
  },
  {
    id: 'nat-743',
    numero: '743/2024', numeroRG: 'RG n° 2024/0743',
    date: '2024-09-09', juridiction: 'Tribunal de commerce de San Pedro', sigle: 'TCSP',
    formation: 'Chambre unique', president: 'M. BAMBA Issouf', greffier: 'M. YÉBOUÉ Koffi',
    matiere: 'Contrats commerciaux', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Résiliation d'un contrat de fourniture pour inexécution",
    synthese: "La résiliation est prononcée aux torts du fournisseur, faute de livraisons conformes dans les délais convenus.",
    parties: [
      { nom: 'SOCIÉTÉ PORTUAIRE DE SAN PEDRO', qualite: 'Demanderesse' },
      { nom: 'AFRICA SUPPLY', qualite: 'Défenderesse' },
    ],
    procedure: [
      { date: '2024-04-15', acte: "Mise en demeure de livrer restée sans effet" },
      { date: '2024-06-10', acte: "Assignation en résiliation du contrat" },
    ],
    demandes: ["prononcer la résiliation du contrat aux torts du fournisseur", "condamner la défenderesse à des dommages-intérêts"],
    textesCites: ["l'Acte uniforme relatif au droit commercial général (art. 251)"],
    dispositif: ["Prononce la résiliation du contrat", "Condamne AFRICA SUPPLY à des dommages-intérêts", "Condamne la défenderesse aux dépens"],
    motsCles: ['résiliation', 'contrat de fourniture', 'inexécution'],
    affaire: 'AFF-PORTSP-AFRICA',
  },
  {
    id: 'nat-323',
    numero: '323/2025', numeroRG: 'RG n° 2025/0323',
    date: '2025-05-26', juridiction: 'Tribunal de commerce de Yamoussoukro', sigle: 'TCY',
    formation: 'Chambre unique', president: 'Mme AHOUSSI Chantal', greffier: 'M. N\u2019DRI Konan',
    matiere: 'Procédures collectives', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Admission de créance au passif d'une liquidation",
    synthese: "La créance déclarée est admise au passif après vérification des pièces justificatives produites.",
    parties: [
      { nom: "SOCIÉTÉ CÔTE D'IVOIRE FINANCE", qualite: 'Demanderesse' },
      { nom: 'SARL BÂTIR PLUS', qualite: 'Défenderesse' },
    ],
    procedure: [
      { date: '2025-02-10', acte: "Déclaration de créance au passif" },
      { date: '2025-04-08', acte: "Audience de vérification des créances" },
    ],
    demandes: ["admettre la créance au passif de la liquidation"],
    textesCites: ["l'Acte uniforme portant organisation des procédures collectives (art. 78)"],
    dispositif: ["Admet la créance au passif pour le montant déclaré", "Réserve les dépens"],
    motsCles: ['procédure collective', 'liquidation', 'créance'],
    affaire: 'AFF-CIF-BATIR',
  },
  {
    id: 'nat-65',
    numero: '65/2026', numeroRG: 'RG n° 2026/0065',
    date: '2026-02-13', juridiction: 'Cour suprême', sigle: 'CS',
    formation: 'Chambre judiciaire', president: 'M. KONAN Brou', greffier: 'Mme ASSI Marie',
    matiere: 'Voies de recours', typeDecision: 'Arrêt', instance: 'Cassation',
    objet: "Rejet du pourvoi pour défaut de moyen recevable",
    synthese: "La chambre judiciaire rejette le pourvoi, les griefs soulevés ne satisfaisant pas aux exigences de précision.",
    parties: [
      { nom: "M. KOFFI N'DRI", qualite: 'Demandeur' },
      { nom: 'SOCIÉTÉ AGRO CI', qualite: 'Défenderesse' },
    ],
    procedure: [
      { date: '2025-10-02', acte: "Pourvoi en cassation formé par le demandeur" },
      { date: '2026-01-15', acte: "Rapport du conseiller rapporteur et avis de l'avocat général" },
    ],
    demandes: ["casser et annuler l'arrêt de la cour d'appel"],
    textesCites: ["la loi organique sur la Cour suprême (art. 28)", "le Code de procédure civile (art. 612)"],
    dispositif: ["Rejette le pourvoi", "Condamne le demandeur aux dépens"],
    motsCles: ['cassation', 'pourvoi', 'irrecevabilité'],
    affaire: 'AFF-KOFFI-AGRO',
  },
  {
    id: 'nat-819',
    numero: '819/2025', numeroRG: 'RG n° 2025/0819',
    date: '2025-10-18', juridiction: "Tribunal de commerce d'Abidjan", sigle: 'TCA',
    formation: '2e chambre', president: 'Mme N\u2019GUESSAN Aya', greffier: 'M. TRAORÉ Issa',
    matiere: 'Baux commerciaux', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Renouvellement du bail commercial et indemnité d'éviction",
    synthese: "Le refus de renouvellement ouvre droit à une indemnité d'éviction calculée selon la valeur du fonds exploité.",
    parties: [
      { nom: "ETS N'DA SERVICES", qualite: 'Demandeur' },
      { nom: 'IMMOBILIÈRE LAGUNE', qualite: 'Défenderesse', avocat: 'Cabinet SORO & Partners' },
    ],
    procedure: [
      { date: '2025-05-02', acte: "Congé notifié par le bailleur" },
      { date: '2025-06-15', acte: "Assignation aux fins de renouvellement" },
      { date: '2025-09-30', acte: "Audience de plaidoirie" },
    ],
    demandes: ["ordonner le renouvellement du bail", "à titre subsidiaire, indemnité d'éviction"],
    textesCites: ["l'Acte uniforme relatif au droit commercial général (art. 91 à 127)"],
    dispositif: ["Dit que le refus de renouvellement ouvre droit à indemnité", "Ordonne une expertise"],
    motsCles: ['bail commercial', 'renouvellement', 'indemnité d\u2019éviction'],
    affaire: 'AFF-NDA-LAGUNE', estInterne: true,
  },
  {
    id: 'nat-508',
    numero: '508/2024', numeroRG: 'RG n° 2024/0508',
    date: '2024-06-21', juridiction: "Cour d'appel de commerce d'Abidjan", sigle: 'CAA',
    formation: '2e chambre', president: 'M. OUATTARA Dramane', greffier: 'Mme COULIBALY Fanta',
    matiere: 'Recouvrement de créance', typeDecision: 'Arrêt', instance: 'Appel',
    objet: "Confirmation d'une ordonnance d'injonction de payer",
    synthese: "La cour confirme l'ordonnance dès lors que la créance est certaine, liquide et exigible.",
    parties: [
      { nom: 'SOCIÉTÉ ORANGE CI', qualite: 'Appelante' },
      { nom: 'ENTREPRISE KOUADIO BTP', qualite: 'Intimée' },
    ],
    procedure: [
      { date: '2024-02-10', acte: "Opposition à l'ordonnance d'injonction de payer" },
      { date: '2024-04-25', acte: "Déclaration d'appel" },
      { date: '2024-06-05', acte: "Audience de plaidoirie" },
    ],
    demandes: ["infirmer l'ordonnance d'injonction de payer"],
    textesCites: ["l'Acte uniforme portant organisation des procédures simplifiées de recouvrement (art. 1 à 18)"],
    dispositif: ["Confirme l'ordonnance d'injonction de payer", "Condamne l'appelante aux dépens"],
    motsCles: ['injonction de payer', 'recouvrement', 'créance'],
    affaire: 'AFF-ORANGE-KOUADIO', estInterne: true,
  },
  {
    id: 'nat-206',
    numero: '206/2026', numeroRG: 'RG n° 2026/0206',
    date: '2026-03-02', juridiction: 'Tribunal de commerce de Man', sigle: 'TCM',
    formation: 'Chambre unique', president: 'M. SORO Lacina', greffier: 'M. BAMBA Ousmane',
    matiere: 'Droit bancaire', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Clôture de compte et information du client",
    synthese: "La banque est tenue d'informer son client des modalités de clôture et des opérations restant à régulariser.",
    parties: [
      { nom: 'M. BAKAYOKO SORO', qualite: 'Demandeur' },
      { nom: 'BANQUE RÉGIONALE', qualite: 'Défenderesse' },
    ],
    procedure: [
      { date: '2025-11-15', acte: "Assignation devant le Tribunal de commerce de Man" },
      { date: '2026-02-10', acte: "Audience de plaidoirie" },
    ],
    demandes: ["condamner la banque à indemniser le client pour clôture abusive"],
    textesCites: ["le Code civil (art. 1134)", "la loi bancaire UEMOA"],
    dispositif: ["Condamne la BANQUE RÉGIONALE à verser des dommages-intérêts", "Condamne la défenderesse aux dépens"],
    motsCles: ['droit bancaire', 'clôture de compte', 'information'],
    affaire: 'AFF-BAKAYOKO-BANQUE',
  },
  {
    id: 'nat-331',
    numero: '331/2024', numeroRG: 'RG n° 2024/0331',
    date: '2024-04-30', juridiction: 'Tribunal de commerce de Daloa', sigle: 'TCD',
    formation: 'Chambre unique', president: 'M. KOUASSI Jean', greffier: 'Mme AHOU Brigitte',
    matiere: 'Assurances', typeDecision: 'Jugement', instance: 'Première instance',
    objet: "Garantie de l'assureur et déclaration du sinistre",
    synthese: "L'assureur doit sa garantie lorsque la déclaration du sinistre intervient dans le délai contractuel.",
    parties: [
      { nom: 'SOCIÉTÉ CACAO PLUS', qualite: 'Demanderesse' },
      { nom: 'ASSURANCES IVOIRE', qualite: 'Défenderesse' },
    ],
    procedure: [
      { date: '2023-12-20', acte: "Déclaration de sinistre adressée à l'assureur" },
      { date: '2024-02-15', acte: "Assignation en paiement de la garantie" },
    ],
    demandes: ["condamner l'assureur à exécuter sa garantie"],
    textesCites: ["le Code CIMA (art. 12 et 13)"],
    dispositif: ["Condamne ASSURANCES IVOIRE à exécuter sa garantie", "Condamne la défenderesse aux dépens"],
    motsCles: ['assurance', 'sinistre', 'garantie'],
    affaire: 'AFF-CACAO-ASSURANCE',
  },
];

/* ────────────────────────── Helpers ────────────────────────── */

function formatDateFR(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateLongFR(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/* ────────────────────────── Composant principal ────────────────────────── */

const JurisprudenceNationaleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const decision = DECISIONS.find(d => d.id === id);

  if (!decision) {
    return (
      <main className="dd-page">
        <button className="dd-retour" onClick={() => navigate('/espace-tca/jurisprudence-nationale')}>
          <ArrowLeft size={16} /> Retour
        </button>
        <div className="dd-introuvable">
          <h1>Décision introuvable</h1>
          <p>Cette référence ne figure pas dans le fonds national.</p>
        </div>
      </main>
    );
  }

  const d = decision;

  /* Décisions du même dossier */
  const decisionsLiees = DECISIONS
    .filter(dec => dec.id !== d.id && dec.affaire === d.affaire)
    .sort((a, b) => b.date.localeCompare(a.date));

  /* Décisions similaires */
  const motsClesCourants = new Set(d.motsCles.map(m => m.toLowerCase()));
  const decisionsSimilaires = DECISIONS
    .filter(dec => dec.id !== d.id && dec.affaire !== d.affaire)
    .map(dec => ({
      dec,
      score: (dec.matiere === d.matiere ? 3 : 0) + dec.motsCles.filter(m => motsClesCourants.has(m.toLowerCase())).length,
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(({ dec }) => dec);

  return (
    <main className="dd-page">
      <button className="dd-retour" onClick={() => navigate('/espace-tca/jurisprudence-nationale')}>
        <ArrowLeft size={16} /> Retour
      </button>

      {/* En-tête */}
      <section className="dd-header">
        <div className="dd-header-text">
          <h1>{d.juridiction} · Décision n° {d.numero}</h1>
          <p>{d.numeroRG} · {formatDateFR(d.date)} · {d.matiere} · {d.instance}</p>
        </div>
        <div className="dd-header-actions">
          <button className="dd-btn-secondary"><Download size={15} /> PDF</button>
        </div>
      </section>

      {/* Badges */}
      <div className="dd-badges">
        <span className={`dd-badge-statut ${d.estInterne ? 'dd-statut-controle' : 'dd-statut-importe'}`}>
          {d.estInterne ? 'Votre juridiction' : 'Fonds national'}
        </span>
      </div>

      {/* Contenu 3 colonnes */}
      <div className="dd-content">
        {/* Marge */}
        <aside className="dd-marge">
          <div className="dd-marge-inner">
            <p className="dd-marge-label">Marge</p>
            <div className="dd-marge-republique">
              <p className="dd-marge-bold">République de Côte d'Ivoire</p>
              <p>Union — Discipline — Travail</p>
              <p className="dd-marge-bold dd-marge-space">{d.juridiction}</p>
              <p className="dd-marge-bold">{d.formation}</p>
            </div>
            <div className="dd-marge-sep" />
            <p className="dd-marge-numero">{d.typeDecision} n° {d.numero}</p>
            <p className="dd-marge-rg">{d.numeroRG}</p>
            <p className="dd-marge-objet">{d.objet}</p>
            <div className="dd-marge-sep" />
            <p className="dd-marge-label-small">Décision</p>
            <div className="dd-marge-dispositif">
              {d.dispositif.map((item, i) => <p key={i}>{item}</p>)}
            </div>
            <div className="dd-marge-mention">
              <p className="dd-marge-label-small">Source</p>
              <p>{d.estInterne ? 'Décision de votre juridiction.' : 'Fonds national de jurisprudence.'}</p>
            </div>
          </div>
        </aside>

        {/* Texte intégral */}
        <article className="dd-texte">
          <header className="dd-texte-header">
            <Scale size={32} className="dd-texte-icon" />
            <p className="dd-texte-juridiction">{d.juridiction}</p>
            <h2 className="dd-texte-numero">{d.typeDecision} n° {d.numero}</h2>
            <p className="dd-texte-objet">{d.objet}</p>
            <p className="dd-texte-date">Audience du {formatDateLongFR(d.date)}</p>
          </header>

          <div className="dd-texte-body">
            <p className="dd-texte-center dd-texte-bold">Contradictoire</p>

            <p>
              À l'audience publique de la {d.formation} de la {d.juridiction}, tenue le{' '}
              {formatDateLongFR(d.date)}, sous la présidence de {d.president}, assisté de{' '}
              {d.greffier}, greffier, il a été rendu la décision suivante.
            </p>

            <p className="dd-texte-bold dd-texte-upper">Entre :</p>
            {d.parties.map((partie, index) => (
              <p key={`${partie.nom}-${partie.qualite}`}>
                <strong>{partie.nom}</strong>, {partie.qualite.toLowerCase()}
                {partie.avocat ? `, ayant pour conseil ${partie.avocat}` : ''}
                {index < d.parties.length - 1 ? ' ;' : '.'}
              </p>
            ))}

            <p className="dd-texte-bold dd-texte-upper">Sur la procédure et les prétentions</p>
            {d.procedure.map((etape, i) => (
              <p key={i}>Attendu que, le {formatDateLongFR(etape.date)}, {etape.acte.toLowerCase()} ;</p>
            ))}
            {d.demandes.map((demande, i) => (
              <p key={i}>Attendu qu'il est demandé à la juridiction de faire droit à la prétention suivante : {demande.toLowerCase()} ;</p>
            ))}

            <p className="dd-texte-bold dd-texte-upper">Le tribunal</p>
            <p>{d.synthese}</p>
            {d.textesCites.map((texte, i) => (
              <p key={i}>Vu {texte} ;</p>
            ))}

            <p className="dd-texte-center dd-texte-bold dd-texte-par-ces-motifs">Par ces motifs</p>
            <p>
              Statuant publiquement, contradictoirement, en matière {d.matiere.toLowerCase()} et conformément à la loi ;
            </p>
            {d.dispositif.map((item, i) => (
              <p key={i}>{item} ;</p>
            ))}
            <p className="dd-texte-prononce">Ainsi fait, jugé et prononcé publiquement les jour, mois et an que dessus.</p>

            <div className="dd-texte-signatures">
              <p className="dd-texte-bold">Le Président</p>
              <p className="dd-texte-bold">Le Greffier</p>
            </div>
          </div>
        </article>

        {/* Sidebar */}
        <aside className="dd-sidebar">
          <section className="dd-sidebar-card dd-sidebar-resume">
            <h3><FileText size={16} /> Résumé</h3>
            <p>{d.synthese}</p>
          </section>

          <section className="dd-sidebar-card">
            <h3><GitBranch size={16} /> Décisions liées</h3>
            {decisionsLiees.length > 0 ? (
              <div className="dd-sidebar-list">
                {decisionsLiees.map(dec => (
                  <button
                    key={dec.id}
                    className="dd-sidebar-link"
                    onClick={() => navigate(`/espace-tca/jurisprudence-nationale/${dec.id}`)}
                  >
                    <div>
                      <p className="dd-sidebar-link-title">{dec.typeDecision} n° {dec.numero}</p>
                      <p className="dd-sidebar-link-meta">{dec.sigle} · {formatDateFR(dec.date)}</p>
                    </div>
                    <ChevronRight size={16} />
                  </button>
                ))}
              </div>
            ) : (
              <p className="dd-sidebar-empty">Aucune autre décision dans cette affaire.</p>
            )}
          </section>

          <section className="dd-sidebar-card">
            <h3><Sparkles size={16} /> Décisions similaires</h3>
            {decisionsSimilaires.length > 0 ? (
              <div className="dd-sidebar-list">
                {decisionsSimilaires.map(dec => (
                  <button
                    key={dec.id}
                    className="dd-sidebar-link"
                    onClick={() => navigate(`/espace-tca/jurisprudence-nationale/${dec.id}`)}
                  >
                    <div>
                      <p className="dd-sidebar-link-title">{dec.typeDecision} n° {dec.numero}</p>
                      <p className="dd-sidebar-link-meta">{dec.matiere} · {dec.objet}</p>
                    </div>
                    <ChevronRight size={16} />
                  </button>
                ))}
              </div>
            ) : (
              <p className="dd-sidebar-empty">Aucune décision similaire identifiée.</p>
            )}
          </section>
        </aside>
      </div>
    </main>
  );
};

export default JurisprudenceNationaleDetail;
